import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User } from '../services/authService';

export interface LogoutResult {
  localOnly: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<LogoutResult>;
  apiVersion: 'v1' | 'v2' | 'buggy';
  setApiVersion: (version: 'v1' | 'v2' | 'buggy') => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [apiVersion, setApiVersion] = useState<'v1' | 'v2' | 'buggy'>('v1');

  useEffect(() => {
    // Load user from localStorage on mount
    const storedUser = authService.getUser();
    if (storedUser) {
      setUser(storedUser);
      setApiVersion('v2'); // If user is logged in, default to V2
    }

    // Load saved API version preference
    const savedVersion = localStorage.getItem('api_version') as 'v1' | 'v2' | 'buggy' | null;
    if (savedVersion) {
      setApiVersion(savedVersion);
    }
  }, []);

  const login = async (username: string, password: string) => {
    const response = await authService.login(username, password);
    setUser({ username: response.username, role: response.role });
    setApiVersion('v2'); // Switch to V2 after login
    localStorage.setItem('api_version', 'v2');
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setApiVersion('v1');
      localStorage.setItem('api_version', 'v1');
      return { localOnly: false };
    } catch (error) {
      setUser(null);
      setApiVersion('v1');
      localStorage.setItem('api_version', 'v1');
      return { localOnly: true };
    }
  };

  const handleSetApiVersion = (version: 'v1' | 'v2' | 'buggy') => {
    setApiVersion(version);
    localStorage.setItem('api_version', version);
    
    // If switching to V2 but not authenticated, do nothing (login modal will handle)
    // If switching from V2 to V1/buggy, keep user logged in but switch API
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        apiVersion,
        setApiVersion: handleSetApiVersion,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
